import React from 'react';
import { useProfile } from '../Contexts/ProfileContext';

// Custom hook for different profile data usage patterns
export const useProfileData = (options = {}) => {
  const {
    requireAuth = true,
    autoFetch = true,
    fallbackData = null
  } = options;

  const { 
    profileData, 
    loading, 
    error, 
    isProfileLoaded, 
    fetchProfile, 
    clearProfile, 
    updateProfileData 
  } = useProfile();

  // Determine if profile data should be available
  const hasProfileData = profileData !== null;
  const shouldShowProfile = requireAuth ? hasProfileData : true;
  
  // Get display data with fallback
  const displayData = profileData || fallbackData;

  // Auto-fetch if needed
  React.useEffect(() => {
    if (autoFetch && !isProfileLoaded) {
      fetchProfile(requireAuth);
    }
  }, [autoFetch, isProfileLoaded, requireAuth, fetchProfile]);

  return {
    profileData: displayData,
    loading,
    error,
    isProfileLoaded,
    hasProfileData,
    shouldShowProfile,
    fetchProfile: () => fetchProfile(requireAuth),
    clearProfile,
    updateProfileData
  };
};

// Hook for pages that don't need profile data at all
export const useNoProfile = () => {
  return {
    profileData: null,
    loading: false,
    error: null,
    isProfileLoaded: true,
    hasProfileData: false,
    shouldShowProfile: true,
    fetchProfile: () => {},
    clearProfile: () => {},
    updateProfileData: () => {}
  };
};

// Hook for pages that need profile data but can work without it
export const useOptionalProfile = (fallbackData = null) => {
  return useProfileData({
    requireAuth: false,
    autoFetch: true,
    fallbackData
  });
};

// Hook for pages that absolutely need profile data
export const useRequiredProfile = () => {
  return useProfileData({
    requireAuth: true,
    autoFetch: true
  });
};
